/* eslint-disable camelcase */
import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { CUSTOM_BRA_BAND_FILTER, CUSTOM_BRA_CUP_FILTER } from '../../constants';
import useBraSizeFilter from '../../hooks/useBraSizeFilter';
import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import { sanitizeFilterName } from '../../utils/filters';

import MobileGridGroupedFilterButtonInput from './MobileGridGroupedFilterButtonInput';

const DynamicFormattedMessage = FormattedMessage;

const GroupListItem = styled.li``;

const SubFacetTitle = styled.h3`
  display: flex;
  padding-bottom: 8px;
  ${({ theme }) => theme.gridFilterStyles.nestedHeadingStyle}
  font-size: 14px;
`;

const CheckboxGroup = styled.fieldset`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  justify-content: space-between;
  border: none;
`;

const HiddenLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

function MobileGridCustomBraFilter({ name, display_name, options = [] }) {
  const { selectedUrlFilters } = useUnifiedProductGridData();

  const {
    availableBandSizes,
    availableCupSizes,
    selectedBandSizeValue,
    setSelectedBandSizeValue,
  } = useBraSizeFilter({ options, facetName: name });

  const { toggleSelectedFacetsToApply, isFacetOptionSelected } =
    useSelectedFacetsToApply();

  const handleOnChange = ({ selectedCupSize }) => {
    const facetOption = `${selectedBandSizeValue}${selectedCupSize}`;

    toggleSelectedFacetsToApply({
      facetName: name,
      facetOption,
    });
  };

  // Both band sizes and cup sizes must be available to render the filter
  if (!availableBandSizes?.length || !availableCupSizes?.length) {
    return null;
  }

  return (
    <>
      <GroupListItem>
        <SubFacetTitle>
          <DynamicFormattedMessage
            id={`site_product_filter.${CUSTOM_BRA_BAND_FILTER.name}`}
            defaultMessage={CUSTOM_BRA_BAND_FILTER.displayName}
          />
        </SubFacetTitle>
        <CheckboxGroup>
          <HiddenLegend>
            <DynamicFormattedMessage
              id={`site_product_filter.${CUSTOM_BRA_BAND_FILTER.name}`}
              defaultMessage={CUSTOM_BRA_BAND_FILTER.displayName}
            />
          </HiddenLegend>
          {availableBandSizes?.map(({ label, value }) => {
            return (
              <MobileGridGroupedFilterButtonInput
                key={value}
                id={`${name}-${value}`}
                name={value}
                label={label}
                checked={selectedBandSizeValue === value}
                onChange={() => setSelectedBandSizeValue(value)}
                filterName={sanitizeFilterName(CUSTOM_BRA_BAND_FILTER.name)}
              />
            );
          })}
        </CheckboxGroup>
      </GroupListItem>
      <GroupListItem>
        <SubFacetTitle>
          <DynamicFormattedMessage
            id={`site_product_filter.${CUSTOM_BRA_CUP_FILTER.name}`}
            defaultMessage={CUSTOM_BRA_CUP_FILTER.displayName}
          />
        </SubFacetTitle>
        <CheckboxGroup>
          <HiddenLegend>
            <DynamicFormattedMessage
              id={`site_product_filter.${CUSTOM_BRA_CUP_FILTER.name}`}
              defaultMessage={CUSTOM_BRA_CUP_FILTER.displayName}
            />
          </HiddenLegend>
          {availableCupSizes?.map(({ label, value }) => {
            const bandCupSizeValue = `${selectedBandSizeValue}${value}`;
            const isSelected = isFacetOptionSelected({
              facetName: name,
              facetOption: bandCupSizeValue,
              selectedUrlFilters,
            });
            return (
              <MobileGridGroupedFilterButtonInput
                key={value}
                id={`${name}-${value}`}
                name={value}
                label={label}
                superScriptLabel={selectedBandSizeValue}
                superScriptCategory="band-size"
                checked={isSelected}
                onChange={() => handleOnChange({ selectedCupSize: value })}
                filterName={sanitizeFilterName(CUSTOM_BRA_CUP_FILTER.name)}
              />
            );
          })}
        </CheckboxGroup>
      </GroupListItem>
    </>
  );
}

MobileGridCustomBraFilter.propTypes = {
  display_name: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
    })
  ),
};

export default MobileGridCustomBraFilter;
